import React, { useMemo, useRef, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { Box } from '@mui/material';

import { isPublicRoute } from '../PublicRoutes';
import Spin from 'components/Spin';
import { useMenu } from 'global/MenuContext';
import { useUser } from 'global/UserContext';
import { useAppInitialization } from 'hooks/useAppInitialization';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { useNavigationSync } from 'hooks/useNavigationSync';
import Error from 'pages/Errors/Error';

interface RouteGuardProps {
  children: React.ReactNode;
}

/**
 * Componente que gerencia toda a lógica de acesso a rotas
 * - Controle de autenticação (token)
 * - Validação de rotas (404)
 * - Redirecionamentos (login, primeira rota válida)
 * - Evita flashes de 404 durante inicialização
 *
 * Fluxo de renderização:
 * 1. Public route? → Render immediately
 * 2. Still loading? → Show spinner
 * 3. No token? → Show spinner (redirect happens in useEffect)
 * 4. Has token but not fully initialized? → Show spinner (wait for menu)
 * 5. Route invalid? → Show 404
 * 6. All good → Render content
 */
const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const location = useLocation();
  const { token } = useUser();
  const { menu } = useMenu();
  const { isFullyInitialized, loadingStates } = useAppInitialization();
  const menuRoutes = useMenuRoutes();
  const { navigateToPath, restoreNavigation } = useNavigationSync();

  // Verifica se o carregamento básico foi concluído (independente de ter dados ou não)
  const isLoadingComplete = !loadingStates.userLoading && !loadingStates.menuLoading;

  // Cache de rotas válidas para evitar validações desnecessárias
  const [validRoutesCache, setValidRoutesCache] = useState<Set<string>>(new Set());
  const hasAttemptedRedirect = useRef(false);
  const lastValidatedPath = useRef<string>('');

  // Verifica se é uma rota pública
  const isPublicRouteCheck = useMemo(() => {
    return isPublicRoute(location.pathname as string);
  }, [location.pathname]);

  // Atualiza cache de rotas válidas quando menuRoutes muda
  useEffect(() => {
    if (menuRoutes.length > 0) {
      const routes = new Set(menuRoutes.map(route => route.path));
      setValidRoutesCache(routes);
    }
  }, [menuRoutes]);

  // Verifica se a rota atual é válida com cache
  const isRouteValid = useMemo(() => {
    const currentPath = location.pathname;

    // Se é rota pública, sempre é válida
    if (isPublicRouteCheck) {
      return true;
    }

    // Durante inicialização, assume que é válida para evitar 404
    if (!isFullyInitialized) {
      return true;
    }

    // Se não tem cache ainda, assume válida temporariamente
    if (validRoutesCache.size === 0) {
      return true;
    }

    // Verifica no cache
    return validRoutesCache.has(currentPath);
  }, [location.pathname, isPublicRouteCheck, isFullyInitialized, validRoutesCache]);

  // CONTROLE DE AUTENTICAÇÃO E REDIRECIONAMENTOS
  useEffect(() => {
    // Aguarda o carregamento básico completar (não precisa ter dados, só terminar de carregar)
    if (!isLoadingComplete) {
      return;
    }

    const currentPath = location.pathname;

    // Se mudou a rota, reseta flag de redirect
    if (currentPath !== lastValidatedPath.current) {
      hasAttemptedRedirect.current = false;
      lastValidatedPath.current = currentPath;
    }

    // Evita múltiplos redirects
    if (hasAttemptedRedirect.current) {
      return;
    }

    // CASO 1: Usuário não autenticado tentando acessar rota protegida
    if (!token && !isPublicRouteCheck) {
      console.log('[RouteGuard] Redirecionando usuário não autenticado para login');
      hasAttemptedRedirect.current = true;
      navigateToPath('/login');
      return;
    }

    // CASO 2: Usuário autenticado em rota pública (exceto logout e event-invite)
    // Só redireciona se estiver TOTALMENTE inicializado (com menu carregado)
    const isEventInvitePath = currentPath.startsWith('/invite/event/');
    if (
      token &&
      isPublicRouteCheck &&
      currentPath !== '/logout' &&
      !isEventInvitePath &&
      isFullyInitialized
    ) {
      console.log('[RouteGuard] Redirecionando usuário autenticado para app');
      hasAttemptedRedirect.current = true;

      // Tenta restaurar navegação anterior
      const restored = restoreNavigation();
      if (!restored) {
        // Se não conseguiu restaurar, vai para a primeira rota válida
        const firstValidRoute = menuRoutes[0];
        if (firstValidRoute?.path) {
          navigateToPath(firstValidRoute.path, firstValidRoute.documentId);
        }
      }
      return;
    }

    // CASO 3: Rota inválida (não pública e não está no cache)
    // Só valida após inicialização completa para evitar false positives
    if (
      !isPublicRouteCheck &&
      isFullyInitialized &&
      validRoutesCache.size > 0 &&
      !validRoutesCache.has(currentPath)
    ) {
      console.log('[RouteGuard] Rota inválida detectada, redirecionando');
      hasAttemptedRedirect.current = true;

      // Tenta restaurar ou vai para primeira rota válida
      const restored = restoreNavigation();
      if (!restored) {
        const firstValidRoute = menuRoutes[0];
        if (firstValidRoute?.path) {
          navigateToPath(firstValidRoute.path, firstValidRoute.documentId);
        }
      }
      return;
    }
  }, [
    isLoadingComplete,
    isFullyInitialized,
    location.pathname,
    token,
    isPublicRouteCheck,
    validRoutesCache,
    menuRoutes,
    navigateToPath,
    restoreNavigation,
  ]);

  // RENDERIZAÇÃO CONDICIONAL

  // 1. Se é rota pública, renderiza imediatamente (não precisa de inicialização completa)
  if (isPublicRouteCheck) {
    return <>{children}</>;
  }

  // 2. Se ainda está carregando (user/menu), mostra loading
  if (!isLoadingComplete) {
    return (
      <Box>
        <Spin />
      </Box>
    );
  }

  // 3. Se não tem token (em rota protegida), mostra loading (redirecionamento em andamento)
  if (!token) {
    return (
      <Box>
        <Spin />
      </Box>
    );
  }

  // 4. Para rotas autenticadas, aguarda inicialização completa (menu carregado)
  if (!isFullyInitialized) {
    return (
      <Box>
        <Spin />
      </Box>
    );
  }

  // 5. Valida se a rota existe
  if (!isRouteValid) {
    return (
      <Error
        title="Página não encontrada!"
        error="404"
        message="A página que você está procurando não existe ou foi movida."
      />
    );
  }

  // 6. Rota válida e autenticada, renderiza normalmente
  return <>{children}</>;
};

export default RouteGuard;
